import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Users, Bot, Search, LayoutGrid, Sparkles, Zap } from 'lucide-react';
import { Container, fadeInUp } from './shared';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

const FLOW_ICONS = [Users, Bot, Search, LayoutGrid, Sparkles];

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const highlights = t('landing.hero.highlights', { returnObjects: true }) as string[];
  const flow = t('landing.hero.flow', { returnObjects: true }) as Record<string, string>;
  const flowLabels = Object.values(flow);

  return (
    <section id="home" className="relative overflow-hidden pb-24 pt-40 sm:pt-48 lg:pb-32">
      {/* Ambient background glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-[-10%] h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-brand-primary/20 blur-[140px]" />
        <div className="absolute right-[-10%] top-[30%] h-[400px] w-[500px] rounded-full bg-brand-secondary/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]" />
      </div>

      <Container className="relative grid gap-16 lg:grid-cols-2 lg:items-center lg:gap-12">
        <div>
          <motion.span
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-secondary backdrop-blur-xl"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t('landing.hero.badge')}
          </motion.span>

          <motion.h1
            variants={fadeInUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className="text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            {t('landing.hero.titleLine1')}<br />{t('landing.hero.titleLine2')}
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className="mt-7 max-w-xl text-lg leading-relaxed text-brand-muted"
          >
            {t('landing.hero.subtitle')}
          </motion.p>

          <motion.ul
            variants={fadeInUp}
            custom={3}
            initial="hidden"
            animate="visible"
            className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {highlights.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-white/90">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-secondary" />
                {item}
              </li>
            ))}
          </motion.ul>

          <motion.div
            variants={fadeInUp}
            custom={4}
            initial="hidden"
            animate="visible"
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <button
              onClick={() => {
                trackEvent(AnalyticsEvents.CTA_CLICK, { location: 'hero', target: '/demo' });
                navigate('/demo');
              }}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-button px-7 py-3.5 text-base font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.03]"
            >
              {t('landing.hero.ctaPrimary')}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
            <a
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-3.5 text-base font-semibold text-white backdrop-blur-xl transition-colors duration-300 hover:bg-white/[0.08]"
            >
              {t('landing.hero.ctaSecondary')}
            </a>
          </motion.div>
        </div>

        {/* Hero visual: animated workflow */}
        <motion.div
          variants={fadeInUp}
          custom={2}
          initial="hidden"
          animate="visible"
          className="relative mx-auto w-full max-w-sm"
        >
          <div className="glass-card relative rounded-3xl p-8">
            <div className="flex flex-col items-center">
              {flowLabels.map((label, i) => {
                const Icon = FLOW_ICONS[i];
                return (
                  <React.Fragment key={label}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }}
                      className="flex w-full items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-5 py-3.5"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-button">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-white">{label}</span>
                    </motion.div>
                    {i < flowLabels.length - 1 && (
                      <motion.div
                        initial={{ scaleY: 0, opacity: 0 }}
                        animate={{ scaleY: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.45 + i * 0.15 }}
                        className="my-1 h-6 w-px origin-top bg-gradient-to-b from-brand-primary to-brand-secondary"
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Floating accent badges for visual depth */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{
              opacity: { duration: 0.6, delay: 1.1 },
              y: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1.1 },
            }}
            className="glass-card absolute -right-6 -top-6 hidden items-center gap-2 rounded-2xl px-4 py-3 shadow-glow-cyan sm:flex"
          >
            <Zap className="h-4 w-4 text-brand-secondary" />
            <span className="text-xs font-semibold text-white">{t('landing.hero.badgeLive')}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: [0, 8, 0] }}
            transition={{
              opacity: { duration: 0.6, delay: 1.3 },
              y: { duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1.3 },
            }}
            className="glass-card absolute -bottom-6 -left-6 hidden items-center gap-2 rounded-2xl px-4 py-3 shadow-glow sm:flex"
          >
            <Check className="h-4 w-4 text-brand-secondary" />
            <span className="text-xs font-semibold text-white">{t('landing.hero.badgeSpeed')}</span>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
};

export default Hero;
